import type { ServiceItemInput } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { ApiError } from '../../../../../shared/shared.api';
import { createServiceItem, deactivateServiceItem, reactivateServiceItem, updateServiceItem, type ServiceItemView } from '../service-items.api';
import { useServiceItemList } from './use-service-item-list';

function toSaveError(err: unknown): string {
  return err instanceof ApiError && err.status === 409 ? 'A service item with this code already exists.' : 'Could not save — check the fields and try again.';
}

/** The service catalogue (`useServiceItemList`) plus create, edit, deactivate and reactivate. A form failure stays inline; row actions toast. */
export function useServiceItems() {
  const { state, list, isRefreshing, reload } = useServiceItemList();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function save(action: () => Promise<unknown>, successMessage: string): Promise<boolean> {
    setSaveError(null);
    setSaving(true);
    try {
      await action();
      await reload();
      toast.success(successMessage);
      return true;
    } catch (err) {
      setSaveError(toSaveError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function rowAction(action: () => Promise<void>, successMessage: string, errorMessage: string): Promise<void> {
    try {
      await action();
      await reload();
      toast.success(successMessage);
    } catch {
      toast.error(errorMessage);
    }
  }

  return {
    state,
    list,
    isRefreshing,
    saveError,
    saving,
    resetSaveError: () => setSaveError(null),
    create: (input: ServiceItemInput) => save(() => createServiceItem(input), `${input.name} created`),
    update: (id: string, input: ServiceItemInput) => save(() => updateServiceItem(id, input), `${input.name} updated`),
    deactivate: (row: ServiceItemView) => rowAction(() => deactivateServiceItem(row.id), `${row.name} deactivated`, `Couldn't deactivate ${row.name}`),
    reactivate: (row: ServiceItemView) => rowAction(() => reactivateServiceItem(row.id), `${row.name} reactivated`, `Couldn't reactivate ${row.name}`),
  };
}
